import React, { useCallback, useEffect, useState } from "react";
import { FaPlus, FaUsers, FaBuilding, FaHistory } from "react-icons/fa";
import DataTable from "../../../components/DataTable";
import StatusBadge from "../../../components/StatusBadge";
import EmptyState from "../../../components/EmptyState";
import { useToast } from "../../../components/Toast";
import { useBuilding } from "../../../context/BuildingContext";
import { colors, spacing, typography, cardBase, pageContainer, pageTitle, pageSubtitle, btnPrimary, btnSecondary } from "../../../utils/designTokens";
import { asambleaAdmin, mensajeDeError, detallesDeError } from "../../../utils/asambleas/api";
import AsambleaFormModal from "./AsambleaFormModal";
import AsambleaDetalle from "./AsambleaDetalle";
import RegistrarRealizadaModal from "./RegistrarRealizadaModal";
import { esRegistrada } from "./registrada";

const fecha = (a) =>
  new Date(Number(a.inicioMs)).toLocaleString("es-UY", {
    timeZone: "America/Montevideo", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

/**
 * Asambleas del edificio seleccionado: lista, alta y detalle. `listar` dice
 * si Zoom automático está disponible (zoomDisponible): con eso el alta ofrece
 * "Zoom automático" además de "Pegar link". Los avisos de Zoom del alta
 * (superposición de la licencia) se muestran en el detalle que se abre después.
 * "Registrar asamblea realizada": una que ya se hizo sin convocarse por
 * Habitia; se abre su detalle para subir la grabación en la sección Acta.
 */
export default function AsambleasAdmin() {
  const { selectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [abierta, setAbierta] = useState(null);
  const [nueva, setNueva] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errorForm, setErrorForm] = useState("");
  const [realizada, setRealizada] = useState(false);
  const [registrando, setRegistrando] = useState(false);
  const [errorRealizada, setErrorRealizada] = useState(null); // {mensaje, campo}
  const [zoomDisponible, setZoomDisponible] = useState(false);
  // Avisos de Zoom que devolvió el alta: { aid, avisos }. Solo los ve el detalle
  // de esa asamblea, la primera vez que se abre (al volver a la lista se descartan).
  const [avisosAlta, setAvisosAlta] = useState(null);
  const buildingId = selectedBuilding ? selectedBuilding.buildingid : null;

  const cargar = useCallback(async () => {
    if (buildingId == null) return;
    setCargando(true);
    try {
      const res = await asambleaAdmin("listar", { buildingId });
      setLista(res.asambleas || []);
      setZoomDisponible(res.zoomDisponible === true);
    } catch (e) {
      showToast(mensajeDeError(e), "error");
    } finally {
      setCargando(false);
    }
  }, [buildingId, showToast]);

  useEffect(() => {
    setAbierta(null);
    cargar();
  }, [cargar]);

  const crear = async (datos) => {
    setGuardando(true);
    setErrorForm("");
    try {
      // proveedorVideo viaja en datos y arriba del pedido, como en el detalle.
      const video = datos && datos.proveedorVideo ? { proveedorVideo: datos.proveedorVideo } : {};
      const res = await asambleaAdmin("guardar", { buildingId, datos, ...video });
      setNueva(false);
      showToast("Borrador guardado. Revisalo y convocá cuando esté listo.", "success");
      await cargar();
      setAvisosAlta(
        res && (res.avisoLicencia || res.avisoCapacidad)
          ? { aid: res.aid, avisos: { avisoLicencia: res.avisoLicencia, avisoCapacidad: res.avisoCapacidad } }
          : null
      );
      setAbierta(res.aid);
    } catch (e) {
      setErrorForm(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  };

  const registrarRealizada = async (datos) => {
    setRegistrando(true);
    setErrorRealizada(null);
    try {
      const res = await asambleaAdmin("registrarRealizada", { buildingId, datos });
      setRealizada(false);
      showToast("Asamblea registrada. Subí la grabación en la sección Acta para armar el acta.", "success");
      await cargar();
      setAvisosAlta(null);
      setAbierta(res.aid);
    } catch (e) {
      setErrorRealizada({ mensaje: mensajeDeError(e), campo: detallesDeError(e).campo || null });
    } finally {
      setRegistrando(false);
    }
  };

  if (!selectedBuilding) {
    return (
      <div style={pageContainer}>
        <EmptyState icon={FaBuilding} title="Elegí un edificio" description="Las asambleas se gestionan por edificio." />
      </div>
    );
  }

  if (abierta) {
    return (
      <div style={pageContainer}>
        <AsambleaDetalle
          aid={abierta}
          edificioNombre={selectedBuilding.name}
          avisosZoomIniciales={avisosAlta && avisosAlta.aid === abierta ? avisosAlta.avisos : null}
          onVolver={() => {
            setAvisosAlta(null);
            setAbierta(null);
          }}
          onCambio={cargar}
        />
      </div>
    );
  }

  const columnas = [
    { key: "inicioMs", label: "Fecha", render: (_, a) => fecha(a) },
    { key: "tipo", label: "Tipo", render: (t) => (t === "extraordinaria" ? "Extraordinaria" : "Ordinaria") },
    { key: "modalidad", label: "Modalidad", render: (m) => ({ presencial: "Presencial", remota: "Remota", mixta: "Mixta" }[m] || m) },
    { key: "estado", label: "Estado", render: (s) => <StatusBadge status={s} size="sm" /> },
    {
      key: "resumen",
      label: "Asistencia",
      render: (r, a) =>
        esRegistrada(a)
          ? "Registrada después (sin citación)"
          : r
            ? `${r.confirmados} asisten · ${r.noAsisten} no · ${r.sinRespuesta} sin respuesta`
            : "—",
    },
  ];

  return (
    <div style={pageContainer}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: spacing[3], flexWrap: "wrap", marginBottom: spacing[6] }}>
        <div>
          <h1 style={{ ...pageTitle, margin: 0 }}>Asambleas</h1>
          <p style={{ ...pageSubtitle, margin: `${spacing[1]}px 0 0` }}>
            Convocá a los propietarios de {selectedBuilding.name}: les llega por WhatsApp o email y confirman asistencia.
          </p>
        </div>
        <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
          <button style={btnSecondary} className="habitia-btn-secondary" onClick={() => { setErrorRealizada(null); setRealizada(true); }}>
            <FaHistory /> Registrar asamblea realizada
          </button>
          <button style={btnPrimary} className="habitia-btn-primary" onClick={() => { setErrorForm(""); setNueva(true); }}>
            <FaPlus /> Nueva asamblea
          </button>
        </div>
      </div>

      <div style={cardBase}>
        {cargando ? (
          <p style={{ margin: 0, color: colors.gray600, fontSize: typography.sizes.md }}>Cargando…</p>
        ) : lista.length === 0 ? (
          <EmptyState
            icon={FaUsers}
            title="Todavía no hay asambleas"
            description="Creá la primera: fecha, modalidad y orden del día."
            action={{ label: "Nueva asamblea", onClick: () => setNueva(true) }}
          />
        ) : (
          <DataTable columns={columnas} data={lista} onRowClick={(a) => setAbierta(a.id)} emptyIcon={FaUsers} emptyTitle="Sin asambleas" />
        )}
      </div>

      <AsambleaFormModal
        open={nueva}
        onClose={() => setNueva(false)}
        onSubmit={crear}
        inicial={null}
        modo="crear"
        edificioNombre={selectedBuilding.name}
        guardando={guardando}
        error={errorForm}
        zoomDisponible={zoomDisponible}
      />

      <RegistrarRealizadaModal
        open={realizada}
        onClose={() => setRealizada(false)}
        onSubmit={registrarRealizada}
        guardando={registrando}
        errorServidor={errorRealizada}
      />
    </div>
  );
}
